import type { BetSelection, SemSelection } from "@/features/betslip/types";
import {
  computeHasAcceptedBets,
  computeIsPlacing,
} from "@/features/betslip/stores/computed";
import type {
  BetslipActions,
  BetslipStore,
  SliceCreator,
  TokenContext,
} from "@/features/betslip/stores/types";
import {
  initialStakesState,
  pruneComboStakes,
  pruneStakesAfterRemoval,
} from "@/features/betslip/stores/slices/stakes";
import {
  initialPromoCashState,
  prunePromoCashByOutcomeIds,
} from "@/features/betslip/stores/slices/promoCash";
import {
  initialTokensState,
  pruneTokensByOutcomeIds,
} from "@/features/betslip/stores/slices/tokens";
import {
  initialUpdatesState,
  pruneUpdatesAfterRemoval,
} from "@/features/betslip/stores/slices/updates";
import {
  exitConfirmingIfMutated,
  invalidateMultiAltStakeRef,
  pruneSubmissionByOutcomeIds,
  reuseSelections,
  resetSubmissionState,
} from "@/features/betslip/stores/slices/submission";
import { isSemSelection } from "@/features/betslip/utils/typeGuards";
import { areRelated, legSetKey } from "@/features/betslip/utils/rules";
import { ALLOW_CROSS_PRODUCT_MULTI } from "@/features/betslip/utils/flags";
import { filterAllocationsForTarget } from "@/features/betslip/utils/filterAllocationsForTarget";

// ─── reconcileTokens ────────────────────────────────────────────────
//
// Reconcile one slot in the tokens slice after a composition mutation:
//   1. Validate the held token. If it's no longer in the eligible set
//      for this target, drop it.
//   2. Claim. If the slot is empty (was empty or just dropped), pick
//      the first eligible allocation that isn't already in use elsewhere.
//
// Scopes:
//   - `single(outcomeId)` — reconciles `tokens.singles[outcomeId]`.
//   - `multi`             — reconciles `tokens.multi`.
//
// SEM leg-change is "reconcile single + reconcile multi" — two scoped
// calls from the caller, no special SEM case here.
//
// Pure function — server data flows in via `tokenContext`. Callers
// (the actions below) get `tokenContext` from their wrapper hook.
// `filterAllocationsForTarget` is called per scope because eligibility
// and `markAsUsed` stamps differ between single and multi targets.
// ────────────────────────────────────────────────────────────────────

export type ReconcileScope =
  | { kind: "single"; outcomeId: string }
  | { kind: "multi" };

export const reconcileTokens = (
  state: BetslipStore,
  scope: ReconcileScope,
  tokenContext: TokenContext,
): BetslipStore["tokens"] => {
  if (!tokenContext.canViewPromos || tokenContext.allocations.length === 0) {
    return state.tokens;
  }
  return scope.kind === "single"
    ? reconcileSingleSlot(state, scope.outcomeId, tokenContext)
    : reconcileMultiSlot(state, tokenContext);
};

const reconcileSingleSlot = (
  state: BetslipStore,
  outcomeId: string,
  tokenContext: TokenContext,
): BetslipStore["tokens"] => {
  const selection = state.selections[outcomeId];
  if (!selection) return state.tokens;

  const candidates = eligibleAllocationsFor(
    state,
    [selection],
    false,
    tokenContext,
  );
  const current = state.tokens.singles[outcomeId];
  if (current && isCandidate(candidates, current)) return state.tokens;

  // Drop the held value first so it doesn't count against itself when
  // looking for a free replacement.
  const singles = { ...state.tokens.singles };
  delete singles[outcomeId];

  // Auto-claim only considers event-based automations. Manual
  // selection via the picker still allows any eligible allocation.
  const autoClaimCandidates = candidates.filter((candidate) =>
    isEventBasedAllocation(candidate, tokenContext.automations),
  );

  const fresh = firstFreeId(
    autoClaimCandidates,
    idsInUse({ ...state.tokens, singles }),
  );
  if (fresh) singles[outcomeId] = fresh;
  return { ...state.tokens, singles };
};

const reconcileMultiSlot = (
  state: BetslipStore,
  tokenContext: TokenContext,
): BetslipStore["tokens"] => {
  const legs = state.multiActiveLegs
    .map((id) => state.selections[id])
    .filter((selection): selection is BetSelection => Boolean(selection));

  if (legs.length < 2) {
    return state.tokens.multi === null
      ? state.tokens
      : { ...state.tokens, multi: null };
  }

  const candidates = eligibleAllocationsFor(state, legs, true, tokenContext);
  const current = state.tokens.multi;
  if (current && isCandidate(candidates, current)) return state.tokens;

  const autoClaimCandidates = candidates.filter((candidate) =>
    isEventBasedAllocation(candidate, tokenContext.automations),
  );

  const fresh = firstFreeId(
    autoClaimCandidates,
    idsInUse({ ...state.tokens, multi: null }),
  );
  return { ...state.tokens, multi: fresh };
};

const isEventBasedAllocation = (
  candidate: { automationId: string },
  automations: TokenContext["automations"],
): boolean => {
  const automation = automations.find(
    (a) => a.automationId === candidate.automationId,
  );
  return Boolean(automation?.attributes?.eventBasedTokens);
};

const eligibleAllocationsFor = (
  state: BetslipStore,
  target: BetSelection[],
  isMulti: boolean,
  tokenContext: TokenContext,
) =>
  filterAllocationsForTarget({
    slipState: state,
    targetSelections: target,
    isMulti,
    allocations: tokenContext.allocations,
    automations: tokenContext.automations,
  });

const isCandidate = (
  candidates: ReturnType<typeof eligibleAllocationsFor>,
  allocationId: string,
): boolean => candidates.some((candidate) => candidate.id === allocationId);

const firstFreeId = (
  candidates: ReturnType<typeof eligibleAllocationsFor>,
  inUse: Set<string>,
): string | null => {
  const free = candidates.find(
    (candidate) => !candidate.usedAtOutcomeId && !inUse.has(candidate.id),
  );
  return free?.id ?? null;
};

const idsInUse = (tokens: BetslipStore["tokens"]): Set<string> => {
  const ids = new Set<string>();
  for (const id of Object.values(tokens.singles)) {
    if (id) ids.add(id);
  }
  if (tokens.multi) ids.add(tokens.multi);
  return ids;
};

const conflictsWithActiveLegs = (
  candidate: BetSelection,
  activeLegOutcomeIds: readonly string[],
  selections: BetslipStore["selections"],
): boolean => {
  for (const outcomeId of activeLegOutcomeIds) {
    const active = selections[outcomeId];
    if (!active) continue;
    if (
      areRelated(candidate, active, {
        allowCrossProduct: ALLOW_CROSS_PRODUCT_MULTI,
      })
    ) {
      return true;
    }
  }
  return false;
};

// Odds and statuses are written per subscription, which for a SEM is its legs.
const subscribedOutcomeIds = (
  selections: BetslipStore["selections"],
): Set<string> => {
  const ids = new Set<string>();
  for (const sel of Object.values(selections)) {
    ids.add(sel.outcomeId);
    if (isSemSelection(sel)) {
      for (const leg of sel.legs) ids.add(leg.outcomeId);
    }
  }
  return ids;
};

export const initialSelectionsState: Pick<
  BetslipStore,
  "selections" | "multiActiveLegs" | "excludedEventIds"
> = {
  selections: {},
  multiActiveLegs: [],
  excludedEventIds: new Set(),
};

type SelectionsActions = Pick<
  BetslipActions,
  | "addSelection"
  | "removeSelection"
  | "removeSelections"
  | "clearSelections"
  | "removeSemLeg"
  | "toggleMultiLeg"
  | "toggleEventDeactivation"
  | "pickMultiLegForEvent"
  | "setSemOdds"
>;

const semDuplicateLegKey = (legs: { outcomeId: string }[]) =>
  legSetKey(legs.map((l) => l.outcomeId));

const findDuplicateSem = (
  candidates: BetSelection[],
  legs: { outcomeId: string }[],
): SemSelection | undefined => {
  const key = semDuplicateLegKey(legs);
  for (const sel of candidates) {
    if (sel.kind !== "sem") continue;
    if (semDuplicateLegKey(sel.legs) === key) return sel;
  }
  return undefined;
};

export const selectionsSlice: SliceCreator<
  typeof initialSelectionsState & SelectionsActions
> = (set, get) => ({
  ...initialSelectionsState,

  addSelection: (selection, tokenContext) => {
    if (computeIsPlacing(get())) return;

    // Block a SEM whose legs are identical to an existing SEM's legs
    if (
      selection.kind === "sem" &&
      findDuplicateSem(Object.values(get().selections), selection.legs)
    ) {
      return;
    }

    set((state) => {
      // If bets are already placed, clear the slip first so the new selection
      // lands in a fresh betslip (matches legacy addSelection).
      const base: BetslipStore = computeHasAcceptedBets(state)
        ? {
            ...state,
            ...initialSelectionsState,
            ...initialStakesState,
            ...initialPromoCashState,
            ...initialTokensState,
            ...initialUpdatesState,
            ...resetSubmissionState(),
          }
        : { ...state, ...exitConfirmingIfMutated(state) };

      // Auto-extend the active multi when the new selection is multiable
      // and doesn't conflict with any leg that's already in. Conflicting
      // (or non-multiable) selections sit in the slip but stay out of
      // the multi until the user explicitly resolves the conflict.
      const shouldJoinMulti =
        selection.isMultiable &&
        !base.multiActiveLegs.includes(selection.outcomeId) &&
        !conflictsWithActiveLegs(
          selection,
          base.multiActiveLegs,
          base.selections,
        );

      // An outright or promo-blocked leg makes the multi ineligible for
      // Bonus Cash — drop the toggle and the multi stake so a promo
      // multi can't be built around it.
      const multiPromoBlocked =
        shouldJoinMulti &&
        base.promoCash.multi &&
        (selection.kind === "outright" || selection.blockPromotionsBets);

      const withSelection: BetslipStore = {
        ...base,
        selections: {
          ...base.selections,
          [selection.outcomeId]: selection,
        },
        multiActiveLegs: shouldJoinMulti
          ? [...base.multiActiveLegs, selection.outcomeId]
          : base.multiActiveLegs,
      };

      const afterMutation: BetslipStore = {
        ...withSelection,
        // A selection that stays out of the multi — non-multiable,
        // conflicting, or on an unticked event — leaves the combination
        // counts alone, so the stakes against them stand.
        stakes: {
          ...pruneComboStakes(base, withSelection).stakes,
          ...(multiPromoBlocked && { multi: 0 }),
        },
        ...(multiPromoBlocked && {
          promoCash: { ...base.promoCash, multi: false },
        }),
      };

      const tokensAfterSingle = reconcileTokens(
        afterMutation,
        { kind: "single", outcomeId: selection.outcomeId },
        tokenContext,
      );

      let tokens = tokensAfterSingle;
      if (shouldJoinMulti) {
        tokens = reconcileTokens(
          { ...afterMutation, tokens: tokensAfterSingle },
          { kind: "multi" },
          tokenContext,
        );
      }

      return {
        ...afterMutation,
        tokens,
        ...invalidateMultiAltStakeRef(),
      };
    });
  },

  removeSelection: (outcomeId, tokenContext) => {
    get().removeSelections([outcomeId], tokenContext);
  },

  removeSelections: (outcomeIds, tokenContext) => {
    if (computeIsPlacing(get())) return;
    set((state) => {
      const drop = new Set(outcomeIds);
      if (drop.size === 0) return state;

      const nextSelections: BetslipStore["selections"] = {};
      let removedAny = false;
      for (const [outcomeId, sel] of Object.entries(state.selections)) {
        if (drop.has(outcomeId)) {
          removedAny = true;
          continue;
        }
        nextSelections[outcomeId] = sel;
      }
      if (!removedAny) return state;

      const nextMultiActiveLegs = state.multiActiveLegs.filter(
        (id) => !drop.has(id),
      );

      const eventIdsRemaining = new Set<string>();
      for (const sel of Object.values(nextSelections)) {
        eventIdsRemaining.add(sel.eventId);
      }
      const outcomeIdsRemaining = subscribedOutcomeIds(nextSelections);
      const nextExcludedEventIds = new Set<string>();
      for (const eventId of state.excludedEventIds) {
        if (eventIdsRemaining.has(eventId)) nextExcludedEventIds.add(eventId);
      }

      const prunedTokens = pruneTokensByOutcomeIds(state, drop);
      const afterMutation: BetslipStore = {
        ...state,
        selections: nextSelections,
        multiActiveLegs: nextMultiActiveLegs,
        excludedEventIds: nextExcludedEventIds,
        tokens: prunedTokens.tokens ?? state.tokens,
      };

      const tokens = reconcileTokens(
        afterMutation,
        { kind: "multi" },
        tokenContext,
      );

      // A placed slip resets; otherwise just prune what left.
      return {
        ...exitConfirmingIfMutated(state),
        selections: nextSelections,
        multiActiveLegs: nextMultiActiveLegs,
        excludedEventIds: nextExcludedEventIds,
        tokens,
        ...pruneUpdatesAfterRemoval(
          state,
          outcomeIdsRemaining,
          eventIdsRemaining,
        ),
        ...(computeHasAcceptedBets(state)
          ? reuseSelections(state, nextSelections)
          : {
              ...pruneStakesAfterRemoval(state, drop, afterMutation),
              ...prunePromoCashByOutcomeIds(state, drop),
              ...pruneSubmissionByOutcomeIds(state, drop),
            }),
      };
    });
  },

  clearSelections: () => {
    if (computeIsPlacing(get())) return;
    set((state) => ({
      ...exitConfirmingIfMutated(state),
      ...initialSelectionsState,
      ...initialStakesState,
      ...initialPromoCashState,
      ...initialTokensState,
      ...initialUpdatesState,
      ...resetSubmissionState(),
    }));
  },

  removeSemLeg: (outcomeId, legOutcomeId, tokenContext) => {
    if (computeIsPlacing(get())) return;
    const sel = get().selections[outcomeId];
    if (!sel || sel.kind !== "sem") return;
    const newLegs = sel.legs.filter((l) => l.outcomeId !== legOutcomeId);
    if (newLegs.length === sel.legs.length) return;

    // Drop the SEM entirely if it falls below the legal leg count.
    // Funnel through removeSelections so the full prune chain runs
    // (stake / promo / token / odds / submission for the SEM's id).
    if (newLegs.length < 2) {
      get().removeSelections([outcomeId], tokenContext);
      return;
    }

    set((state) => {
      // The level described the generated multi; dropping a leg is the user
      // overriding it.
      const adjustedSem: SemSelection = {
        ...sel,
        legs: newLegs,
        ...(sel.mysteryMulti && { mysteryMulti: { level: null } }),
      };
      const nextSelections = {
        ...state.selections,
        [outcomeId]: adjustedSem,
      };

      // The pending price and any rejection belong to the old leg set, so the
      // SEM's own id is dropped alongside the leg's. No event left the slip,
      // so event statuses all stay.
      const drop = new Set([outcomeId, legOutcomeId]);
      const keep = subscribedOutcomeIds(nextSelections);
      for (const id of drop) keep.delete(id);
      const stalePruned = {
        ...pruneUpdatesAfterRemoval(state, keep),
        ...pruneSubmissionByOutcomeIds(state, drop),
      };
      const stateWithoutStale = { ...state, ...stalePruned };

      const afterMutation: BetslipStore = {
        ...stateWithoutStale,
        selections: nextSelections,
      };

      const tokensAfterSingle = reconcileTokens(
        afterMutation,
        { kind: "single", outcomeId },
        tokenContext,
      );
      const tokens = reconcileTokens(
        { ...afterMutation, tokens: tokensAfterSingle },
        { kind: "multi" },
        tokenContext,
      );

      // New leg set, so not the bet that was placed. Same reset as removal —
      // reuseSelections rewrites selections/odds/results, but not
      // marketStatuses, and nothing resubscribes to the dropped leg, so its
      // status has to be pruned here or it goes stale forever.
      if (computeHasAcceptedBets(state)) {
        return {
          ...exitConfirmingIfMutated(state),
          tokens,
          marketStatuses: stalePruned.marketStatuses,
          ...reuseSelections(stateWithoutStale, afterMutation.selections),
        };
      }

      return {
        ...exitConfirmingIfMutated(state),
        selections: afterMutation.selections,
        tokens,
        ...stalePruned,
      };
    });
  },

  toggleMultiLeg: (outcomeId, tokenContext) => {
    set((state) => {
      const selection = state.selections[outcomeId];
      if (!selection || !selection.isMultiable) return state;

      const isActive = state.multiActiveLegs.includes(outcomeId);
      if (isActive) {
        const nextLegs = state.multiActiveLegs.filter((id) => id !== outcomeId);
        const afterMutation: BetslipStore = {
          ...state,
          multiActiveLegs: nextLegs,
        };
        const tokens = reconcileTokens(
          afterMutation,
          { kind: "multi" },
          tokenContext,
        );
        const nextLegRejections = { ...state.multiLegRejections };
        delete nextLegRejections[outcomeId];

        return {
          ...exitConfirmingIfMutated(state),
          multiActiveLegs: nextLegs,
          multiLegRejections: nextLegRejections,
          // Stale combos go; the multi stake needs two picks left.
          ...pruneStakesAfterRemoval(state, null, afterMutation),
          tokens,
        };
      }

      // don't allow to toggle it on if it conflicts with existing active leg
      if (
        conflictsWithActiveLegs(
          selection,
          state.multiActiveLegs,
          state.selections,
        )
      ) {
        return state;
      }

      const afterMutation: BetslipStore = {
        ...state,
        multiActiveLegs: [...state.multiActiveLegs, outcomeId],
      };
      const tokens = reconcileTokens(
        afterMutation,
        { kind: "multi" },
        tokenContext,
      );
      return {
        ...exitConfirmingIfMutated(state),
        multiActiveLegs: afterMutation.multiActiveLegs,
        // New leg set — combination counts changed, so combo stakes reset.
        ...pruneStakesAfterRemoval(state, null, afterMutation),
        tokens,
      };
    });
  },

  toggleEventDeactivation: (eventId, tokenContext) => {
    set((state) => {
      const nextExcluded = new Set(state.excludedEventIds);
      if (nextExcluded.has(eventId)) {
        nextExcluded.delete(eventId);
      } else {
        nextExcluded.add(eventId);
      }

      const afterMutation: BetslipStore = {
        ...state,
        excludedEventIds: nextExcluded,
      };
      const tokens = reconcileTokens(
        afterMutation,
        { kind: "multi" },
        tokenContext,
      );
      return {
        ...exitConfirmingIfMutated(state),
        excludedEventIds: nextExcluded,
        tokens,
        // Combo stakes go. The multi stake parks — this checkbox comes back.
        ...pruneComboStakes(state, afterMutation),
        ...invalidateMultiAltStakeRef(),
      };
    });
  },

  pickMultiLegForEvent: (outcomeId, tokenContext) => {
    set((state) => {
      const selection = state.selections[outcomeId];
      if (!selection || !selection.isMultiable) return state;

      const sameEventActiveId = state.multiActiveLegs.find((id) => {
        const sel = state.selections[id];
        return sel && sel.eventId === selection.eventId;
      });

      const eventIsDeactivated = state.excludedEventIds.has(selection.eventId);

      if (sameEventActiveId === outcomeId && !eventIsDeactivated) return state;

      const otherEventActive = state.multiActiveLegs.filter((id) => {
        const sel = state.selections[id];
        // Other events only, and deactivated events don't count — their picks
        // linger in multiActiveLegs but aren't live in the multi.
        return (
          !!sel &&
          sel.eventId !== selection.eventId &&
          !state.excludedEventIds.has(sel.eventId)
        );
      });

      if (
        conflictsWithActiveLegs(selection, otherEventActive, state.selections)
      ) {
        return state;
      }

      const nextMultiActiveLegs = sameEventActiveId
        ? state.multiActiveLegs.map((id) =>
            id === sameEventActiveId ? outcomeId : id,
          )
        : [...state.multiActiveLegs, outcomeId];

      const nextExcluded = new Set(state.excludedEventIds);
      nextExcluded.delete(selection.eventId);

      // The swapped-out leg's rejection banner no longer applies.
      const nextLegRejections = { ...state.multiLegRejections };
      if (sameEventActiveId && sameEventActiveId !== outcomeId) {
        delete nextLegRejections[sameEventActiveId];
      }

      const afterMutation: BetslipStore = {
        ...state,
        multiActiveLegs: nextMultiActiveLegs,
        excludedEventIds: nextExcluded,
      };
      const tokens = reconcileTokens(
        afterMutation,
        { kind: "multi" },
        tokenContext,
      );
      return {
        ...exitConfirmingIfMutated(state),
        multiActiveLegs: nextMultiActiveLegs,
        excludedEventIds: nextExcluded,
        multiLegRejections: nextLegRejections,
        tokens,
        // Picking swaps this event's leg, so combo stakes go. It never drops
        // a pick, so the multi stake is safe.
        ...pruneComboStakes(state, afterMutation),
        ...invalidateMultiAltStakeRef(),
      };
    });
  },

  setSemOdds: (outcomeId, newOdds) => {
    set((state) => {
      const sel = state.selections[outcomeId];
      if (!sel || sel.kind !== "sem") return state;
      if (sel.odds === newOdds) return state;
      // A SEM re-prices straight into the selection, with no pending-change
      // step to accept, so the price at the confirm would silently differ
      // from the price submitted.
      return {
        ...exitConfirmingIfMutated(state),
        selections: {
          ...state.selections,
          [outcomeId]: { ...sel, odds: newOdds },
        },
      };
    });
  },
});
